import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import { useState } from "react";
import { api } from "../../lib/api";
import { Modal } from "../shared/Modal";
import { ConfirmDialog } from "../common/ConfirmDialog";
import type { CurrentUser } from "../../lib/auth";
import { SavedNote, CheckIcon, AlertTriangleIcon, XIcon } from "../shared/Icons";
import { ExecModal, ExecRow, ChannelBadge, execMeta, fmtDate as fmtDateSec } from "../shared/ExecModal";

const _authedRoute = getRouteApi("/_authed");

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  display: "block",
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

type ErrorAlert = {
  id: string;
  status: "active" | "pending" | "approved";
  opened_at: string | null;
  dismiss_reason: string | null;
  dismissed_by_name: string | null;
  dismissed_at: string | null;
  admin_note: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
};

const BADGE_STYLE = {
  active:   { bg: "var(--brand-soft)",        border: "var(--danger)",             title: "var(--danger)",     label: "Surto de erros ativo" },
  pending:  { bg: "var(--pending-soft)",    border: "var(--pending)",     title: "var(--pending)",     label: "Aguardando aprovação do admin" },
  approved: { bg: "var(--ok-soft)",           border: "var(--ok-line)",            title: "var(--ok)",   label: "Resolvido" },
} as const;

const actBtn = (bg: string): React.CSSProperties => ({
  flexShrink: 0, background: bg, color: "#fff", border: "none", borderRadius: 8,
  padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer",
});
const reasonBox: React.CSSProperties = {
  fontSize: 12, color: "var(--ink-secondary)", background: "var(--surface-card)",
  border: "1px solid var(--line)", borderRadius: 6, padding: "8px 10px", lineHeight: 1.5,
};

function AlertBadge({ alert, onOpen }: { alert: ErrorAlert; onOpen: () => void }) {
  const s = BADGE_STYLE[alert.status];
  const sub = alert.status === "active"
    ? `5+ erros em 10 min · aberto em ${fmtDate(alert.opened_at)}`
    : alert.status === "pending"
      ? `dismiss por ${alert.dismissed_by_name ?? "—"} em ${fmtDate(alert.dismissed_at)}`
      : `resolvido por ${alert.approved_by_name ?? "—"} em ${fmtDate(alert.approved_at)}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={alert.status === "active" ? "alert-pulse" : undefined}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
        background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer",
      }}
    >
      <span style={{ display: "inline-flex", lineHeight: 1, color: s.title }}>{alert.status === "approved" ? <CheckIcon size={16} /> : <AlertTriangleIcon size={16} />}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: s.title }}>{s.label}</div>
        <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>{sub}</div>
      </div>
      <span style={{ fontSize: 18, color: s.title, lineHeight: 1 }}>›</span>
    </button>
  );
}

export function TabGeral({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const [name, setName] = useState(agent.name ?? "");
  const [selected, setSelected] = useState<ErrorAlert | null>(null);
  const [actionKind, setActionKind] = useState<"dismiss" | "resolve" | null>(null);
  const [actText, setActText] = useState("");
  const [execOpen, setExecOpen] = useState<ExecRow | null>(null);
  const closePopup = () => { setSelected(null); setActionKind(null); setActText(""); };
  const openAlert = (a: ErrorAlert) => { setActText(""); setActionKind(null); setSelected(a); };

  const ctx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const isAdmin = ctx.user?.role_name === "Admin";
  const isViewer = !!ctx.user?.permissions?.viewer;
  // Colocação em pasta é POR USUÁRIO: qualquer um que enxerga o agente organiza a
  // PRÓPRIA visão (o dono numa pasta, um participante em outra), sem afetar os demais
  // nem o agente. Só viewer (somente-leitura) não mexe.
  const canFolder = !isViewer;

  const { data: myFolders = [] } = useQuery<{ id: string; name: string; owner_id: string }[]>({
    queryKey: ["agent-folders"],
    queryFn: () => api.get("/agents/folders"),
    enabled: canFolder,
  });

  const folderMut = useMutation({
    mutationFn: (folder_id: string | null) => api.put(`/agents/${agent.id}/folder`, { folder_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      qc.invalidateQueries({ queryKey: ["agents"] });
      qc.invalidateQueries({ queryKey: ["agent-folders"] });
    },
  });

  const { data: alertData } = useQuery<{ alerts: ErrorAlert[] }>({
    queryKey: ["agent-alerts", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/alerts`),
    refetchInterval: 20_000,
  });
  const alerts = alertData?.alerts ?? [];
  // Execuções que causaram o surto (janela de 10 min) + quantas vieram depois.
  const { data: burstData, isError: burstError } = useQuery<{ items: ExecRow[]; after_count: number }>({
    queryKey: ["agent-alert-execs", agent.id, selected?.id],
    queryFn: () => api.get(`/agents/${agent.id}/alerts/${selected!.id}/executions`),
    enabled: !!selected,
  });
  const openAlerts = alerts.filter((a) => a.status !== "approved");   // topo
  const greenAlerts = alerts.filter((a) => a.status === "approved");  // fim da página

  const { data: publications = [] } = useQuery<any[]>({
    queryKey: ["agent-publications", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/publications`),
  });

  // Restauração do agente inteiro (rollback de publicação) — 2 popups de confirmação.
  const [restore, setRestore] = useState<{ pub: any; step: 1 | 2 } | null>(null);
  const restoreMut = useMutation({
    mutationFn: (pubId: string) => api.post(`/agents/${agent.id}/publications/${pubId}/restore`, {}),
    onSuccess: () => { setRestore(null); qc.invalidateQueries(); },  // tudo do agente mudou
    onError: () => setRestore(null),
  });

  const mut = useMutation({
    mutationFn: () =>
      api.patch(`/agents/${agent.id}`, { name }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agent", agent.id] }),
  });

  const actMut = useMutation({
    mutationFn: () =>
      api.post(`/agents/${agent.id}/alerts/${selected!.id}/${actionKind}`, { text: actText }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent-alerts", agent.id] });
      qc.invalidateQueries({ queryKey: ["agents-alerts"] });
      closePopup();
    },
  });

  return (
    <div
      style={{
        maxWidth: 480,
        display: "flex",
        flexDirection: "column",
        gap: 16,
        padding: 24,
      }}
    >
      {/* Abertos (vermelho/laranja) no topo. Verdes vão pro fim da página. */}
      {openAlerts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {openAlerts.map((a) => (
            <AlertBadge key={a.id} alert={a} onOpen={() => openAlert(a)} />
          ))}
        </div>
      )}

      {selected && (
        <Modal onClose={closePopup} zIndex={1000}>
          <div style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 460, padding: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            {actionKind === null ? (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: BADGE_STYLE[selected.status].title }}>
                    {BADGE_STYLE[selected.status].label}
                  </div>
                  <button aria-label="Fechar" onClick={closePopup} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0 }}><XIcon size={16} /></button>
                </div>
                <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                  5+ erros em 10 min · aberto em {fmtDate(selected.opened_at)}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-secondary)" }}>Execuções do surto</div>
                  {burstError ? (
                    <div style={{ fontSize: 11, color: "var(--danger)" }}>Não foi possível carregar as execuções.</div>
                  ) : !burstData ? (
                    <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>Carregando…</div>
                  ) : burstData.items.length === 0 ? (
                    <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>Nenhuma execução encontrada na janela.</div>
                  ) : (
                    <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
                      {burstData.items.map((e) => (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => setExecOpen(e)}
                          style={{ ...reasonBox, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", textAlign: "left", padding: "6px 10px" }}
                        >
                          <span style={{ fontSize: 10.5, fontWeight: 700, color: execMeta(e.status).color, flexShrink: 0 }}>{execMeta(e.status).label}</span>
                          <ChannelBadge channel={e.channel} />
                          <span style={{ flex: 1 }}>{fmtDateSec(e.started_at)}</span>
                          <span style={{ fontFamily: "monospace", fontSize: 10.5, color: "var(--ink-dim)" }}>{e.id.slice(0, 8)}</span>
                          <span style={{ color: "var(--ink-dim)" }}>›</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {!!burstData?.after_count && (
                    <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>+{burstData.after_count} erro{burstData.after_count > 1 ? "s" : ""} após este alerta</div>
                  )}
                </div>
                {selected.dismiss_reason && (
                  <div style={reasonBox}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{selected.dismissed_by_name ?? "Gestor"}:</div>
                    {selected.dismiss_reason}
                    <div style={{ fontSize: 10.5, color: "var(--ink-dim)", marginTop: 4 }}>em {fmtDate(selected.dismissed_at)}</div>
                  </div>
                )}
                {selected.admin_note && (
                  <div style={reasonBox}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>Admin ({selected.approved_by_name ?? "—"}):</div>
                    {selected.admin_note}
                    <div style={{ fontSize: 10.5, color: "var(--ink-dim)", marginTop: 4 }}>em {fmtDate(selected.approved_at)}</div>
                  </div>
                )}
                {selected.status === "active" && isAdmin && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => setActionKind("resolve")} style={actBtn("var(--danger)")}>Resolver</button>
                  </div>
                )}
                {selected.status === "active" && !isAdmin && !isViewer && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => setActionKind("dismiss")} style={actBtn("var(--danger)")}>Dar dismiss</button>
                  </div>
                )}
                {selected.status === "pending" && isAdmin && (
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => setActionKind("resolve")} style={actBtn("var(--pending)")}>Aprovar</button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 15, fontWeight: 700 }}>
                  {actionKind === "dismiss"
                    ? "Dar dismiss no alerta de erros"
                    : selected.status === "pending" ? "Aprovar dismiss" : "Resolver alerta (direto)"}
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.5 }}>
                  {actionKind === "dismiss"
                    ? "Descreva o que você ajustou para conter os erros. Vai para aprovação de um admin."
                    : selected.status === "pending"
                      ? "Confirme o que você verificou antes de aprovar. Fica registrado no histórico."
                      : "Você está resolvendo direto (sem passar pela fila). Descreva o ajuste/verificação."}
                </div>
                <textarea
                  autoFocus
                  value={actText}
                  onChange={(e) => setActText(e.target.value)}
                  placeholder="Mínimo de 50 caracteres…"
                  rows={4}
                  style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
                />
                <div style={{ fontSize: 11, color: actText.trim().length >= 50 ? "var(--ok)" : "var(--ink-muted)" }}>
                  {actText.trim().length}/50 caracteres
                </div>
                {actMut.isError && (
                  <span style={{ fontSize: 12, color: "var(--danger)" }}>
                    {((actMut.error as { payload?: { detail?: unknown } })?.payload?.detail as string) ?? "Não foi possível concluir."}
                  </span>
                )}
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <button
                    onClick={() => { setActionKind(null); setActText(""); }}
                    style={{ background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}
                  >
                    Voltar
                  </button>
                  <button
                    onClick={() => actMut.mutate()}
                    disabled={actMut.isPending || actText.trim().length < 50}
                    style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: actMut.isPending || actText.trim().length < 50 ? 0.6 : 1 }}
                  >
                    {actMut.isPending ? "Enviando…" : "Confirmar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Fora do Modal do alerta: Esc/backdrop do ExecModal não fecham o alerta junto. */}
      {execOpen && (
        <ExecModal
          exec={execOpen}
          stepsUrl={`/agents/${agent.id}/executions/${execOpen.id}/steps`}
          onClose={() => setExecOpen(null)}
          nested
        />
      )}

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>Nome do agente</label>
        <input
          style={isViewer ? { ...inputStyle, color: "var(--ink-muted)", cursor: "not-allowed" } : inputStyle}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome do agente"
          readOnly={isViewer}
          disabled={isViewer}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column" }}>
        <label style={labelStyle}>ID do Agente</label>
        <input
          style={{ ...inputStyle, color: "var(--ink-muted)", cursor: "not-allowed" }}
          value={agent.agent_external_id ?? ""}
          readOnly
          disabled
        />
      </div>

      {canFolder && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Pasta</label>
          <select
            style={{ ...inputStyle, cursor: folderMut.isPending ? "wait" : "pointer" }}
            value={agent.folder_id ?? ""}
            disabled={folderMut.isPending}
            onChange={(e) => folderMut.mutate(e.target.value || null)}
          >
            <option value="">Sem pasta</option>
            {myFolders.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <span style={{ fontSize: 11, color: "var(--ink-dim)", marginTop: 4 }}>
            Crie pastas na aba Agentes. Alterar aqui salva na hora.
          </span>
          {folderMut.error && (
            <span style={{ fontSize: 11, color: "var(--danger)", marginTop: 4 }}>
              {(folderMut.error as { payload?: { detail?: string } }).payload?.detail ?? "Não foi possível alterar a pasta."}
            </span>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Data de criação</label>
          <span style={{ fontSize: 13, color: "var(--ink)" }}>{fmtDate(agent.created_at)}</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <label style={labelStyle}>Última atualização</label>
          <span style={{ fontSize: 13, color: "var(--ink)" }}>{fmtDate(agent.updated_at)}</span>
        </div>
      </div>

      {!isViewer && (<>
      <button
        onClick={() => mut.mutate()}
        disabled={mut.isPending}
        style={{
          background: "var(--brand)",
          color: "#fff",
          border: "none",
          borderRadius: 8,
          padding: "8px 20px",
          fontSize: 13,
          cursor: "pointer",
          alignSelf: "flex-start",
        }}
      >
        {mut.isPending ? "Salvando..." : "Salvar"}
      </button>

      <SavedNote show={mut.isSuccess} />
      </>)}

      {/* Restaurar versão — as 5 publicações anteriores à que está rodando. Modelo/global não tem. */}
      {!isViewer && !agent.is_template && publications.length > 1 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          <label style={labelStyle}>Restaurar versão do agente</label>
          <span style={{ fontSize: 12, color: "var(--ink-dim)", lineHeight: 1.5 }}>
            Volta o agente inteiro para uma publicação anterior e republica na hora. Para voltar só o prompt, use o histórico do prompt.
          </span>
          {publications.slice(1, 6).map((p: any) => (
            <div key={p.id} style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>v{p.version}</span>
              <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{fmtDate(p.created_at)} · {p.published_by_name ?? "—"}</span>
              <button
                type="button"
                disabled={restoreMut.isPending}
                onClick={() => setRestore({ pub: p, step: 1 })}
                style={{ marginLeft: "auto", background: "none", color: "var(--danger)", border: "1px solid var(--danger)", borderRadius: 8, padding: "4px 12px", fontSize: 12, cursor: "pointer" }}
              >
                Restaurar
              </button>
            </div>
          ))}
          {restoreMut.isError && (
            <span style={{ fontSize: 12, color: "var(--danger)" }}>
              {((restoreMut.error as { payload?: { detail?: unknown } })?.payload?.detail as string) ?? "Não foi possível restaurar."}
            </span>
          )}
        </div>
      )}
      {restore?.step === 1 && (
        <ConfirmDialog
          title={`Restaurar a v${restore.pub.version}?`}
          message={`O agente inteiro volta para a v${restore.pub.version} e é republicado em produção NA HORA, como uma versão nova: prompts, modelo, tools, follow-up, avançadas, voz e portais. As listas de whitelist não mudam. A versão atual fica no histórico.`}
          confirmLabel="Continuar"
          onConfirm={() => setRestore({ ...restore, step: 2 })}
          onCancel={() => setRestore(null)}
        />
      )}
      {restore?.step === 2 && (
        <ConfirmDialog
          title="Tem certeza?"
          message={`Afeta as conversas em andamento agora mesmo.${agent.has_unpublished_changes ? " As alterações não publicadas do painel serão PERDIDAS." : ""}`}
          confirmLabel={restoreMut.isPending ? "Restaurando..." : "Restaurar agora"}
          danger
          onConfirm={() => { if (!restoreMut.isPending) restoreMut.mutate(restore.pub.id); }}
          onCancel={() => setRestore(null)}
        />
      )}

      {/* Histórico de versões */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
        <label style={labelStyle}>Histórico de versões</label>
        {publications.length === 0 ? (
          <span style={{ fontSize: 12, color: "var(--ink-dim)" }}>Nenhuma versão publicada ainda.</span>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {publications.map((p: any, i: number) => (
              <div key={i} style={{ background: "var(--surface-card)", border: "1px solid var(--line)", borderRadius: 8, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--ink)" }}>v{p.version}</span>
                  <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{fmtDate(p.created_at)}</span>
                  <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>· {p.published_by_name ?? "—"}</span>
                </div>
                {p.changelog && (
                  <div style={{ fontSize: 12, color: "var(--ink-mid)", lineHeight: 1.5 }}>{p.changelog}</div>
                )}
                {Array.isArray(p.changed_items) && p.changed_items.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {p.changed_items.map((item: string, j: number) => (
                      <span key={j} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)", border: "1px solid var(--brand)" }}>
                        {item}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alertas de erro resolvidos (verde) — no fim da página, abaixo do histórico. */}
      {greenAlerts.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
          <label style={labelStyle}>Histórico de alertas de erro</label>
          {greenAlerts.map((a) => (
            <AlertBadge key={a.id} alert={a} onOpen={() => openAlert(a)} />
          ))}
        </div>
      )}
    </div>
  );
}
